import { useEffect } from 'react';
import Layout from '../../../components/layout/Layout';
import Breadcrumb from '../../../components/layout/Breadcrumb';
import { updateSEO, seoData } from '../../../utils/seo';

// ===== 執事行動規範（route=/standards/conduct）=====
// デザイン土台: 紺#1a2942 / 真鍮金#a38e65 / BTN-08 / 紺帯番号バンド / 白リッチカード / 細罫エディトリアル行
// /standards/hospitality と同型セクションの使い回しはしない（構成を変えた新バリエーション）
// 事実正典: 創立1890年（明治23年）/ 法人設立2025年2月19日 / 代表理事 石井裕一（三代目頭取）/
//   資格は初級Associate・中級Professional・上級Masterの3段階+専門資格
// 創作した年号・数値・実績・違反件数・罰則は書かない

export default function StandardsConductPage() {
  useEffect(() => {
    updateSEO(seoData['/standards/conduct']);
  }, []);

  const breadcrumbs = [
    { label: 'ホーム', href: '/' },
    { label: '基準・指針', href: '/standards' },
    { label: '執事行動規範' }
  ];

  const BTN08_ON_LIGHT =
    "relative inline-flex items-center justify-center bg-[#1a2942] text-[#f8f6f0] border border-[#a38e65] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#a38e65] hover:text-[#1a2942] hover:border-[#1a2942] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#a38e65]/45 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#1a2942]/45";
  const BTN08_ON_NAVY =
    "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";

  const reasonPillars = [
    {
      en: 'Clarity',
      ja: '判断の座標',
      text: '現場では想定外が日常です。規範は細則の羅列ではなく、迷ったときに立ち返る座標として機能します。個人の気質に委ねすぎない共通言語が、家の品位を守ります。'
    },
    {
      en: 'Continuity',
      ja: '奉仕の連続',
      text: '担当が代わっても質が途切れないよう、受け方・伝え方・残し方を揃えます。属人的な勘だけに依存しないことが、依頼者の安心につながります。'
    },
    {
      en: 'Dignity',
      ja: '職能の品位',
      text: '行動規範は監視のための規則ではありません。自らを律し、他者の領域を侵さず、職務に集中するための品位の型です。'
    }
  ];

  const instructionSteps = [
    {
      no: '01',
      en: 'Listen',
      ja: '最後まで聴く',
      text: '途中で解釈を挟まず、指示の目的・期限・優先度・関係者を最後まで受け止めます。聞き漏らした点は、相手の話が区切れたあとにまとめて確認します。'
    },
    {
      no: '02',
      en: 'Restate',
      ja: '要点を復唱する',
      text: '「〜を、〜までに、〜の形で」と短く復唱し、認識のずれをその場で解消します。曖昧なまま動き始めることが、後の手戻りと信頼の損失を生みます。'
    },
    {
      no: '03',
      en: 'Scope',
      ja: '範囲と権限を確かめる',
      text: '自分の判断で進めてよい範囲と、途中報告が必要な分岐を確認します。推測で権限を越えないことが、行動規範の中核です。'
    },
    {
      no: '04',
      en: 'Begin',
      ja: '着手と初動報告',
      text: '着手の目安と、最初の区切りで何を返すかを共有してから動きます。静かに進めることと、進捗を隠すことは同義ではありません。'
    }
  ];

  const houRenSou = [
    {
      title: '報告',
      lead: '事実を先に、評価を後に',
      body: '完了・遅延・異変は、感想より先に事実を伝えます。いつ・何が・いまどの状態かを短く置き、必要なら次の選択肢を添えます。良い話だけを選別しない誠実さが信頼を育てます。'
    },
    {
      title: '連絡',
      lead: '関係者が同じ地図を持つ',
      body: '関係する人へ、同じ粒度の情報を適切な順で届けます。伝えたつもり・聞いていない、を防ぐため、口頭の要点は必要に応じて簡潔な記録で補強します。'
    },
    {
      title: '相談',
      lead: '早めに、論点を整理して',
      body: '判断に迷う点は、抱え込まず早めに相談します。その際は状況・選択肢・自分の暫定案を整え、相手の時間を尊重した形で問いを渡します。'}
  ];

  const timePrepAxes = [
    {
      label: '約束時刻の守り方',
      lines: [
        '到着は「ちょうど」ではなく、余裕を含んだ設計で考える',
        '遅延が見えた時点で、理由より先に影響と代替を伝える',
        '相手を待たせる前提の予定は、あらかじめ合意を取る'
      ]
    },
    {
      label: '事前準備の型',
      lines: [
        '動線・用具・連絡先・天候や交通の変数を前夜までに洗う',
        '当日朝にしかできない確認と、前倒しできる作業を分ける',
        '備えすぎて場を固くしない。使う直前まで目立たせない'
      ]
    },
    {
      label: '変更への備え',
      lines: [
        '予定が動いたときのための次善策を一つ以上心に置く',
        '変更の指示は復唱し、旧情報の破棄を関係者へ明示する',
        '焦りで省略した確認が、最大の遅延になることを忘れない'
      ]
    }
  ];

  const separationNotes = [
    {
      side: '職務側に置くもの',
      items: [
        '依頼者の情報、鍵や機材の扱い、業務上の連絡手段',
        '勤務中の判断基準と、家の流儀に基づく応対の型',
        '業務記録・引き継ぎに必要な事実のみ'
      ]
    },
    {
      side: '私物側に残すもの',
      items: [
        '私的な予定・交友・趣味の話題を業務空間へ持ち込まない',
        '個人の端末や所持品は、許可された範囲と場所に限定する',
        '職務で得た知見を、私的な自己演出の材料にしない'
      ]
    }
  ];

  const relationScenes = [
    {
      en: 'Guests',
      ja: '来客への応対',
      text: '来客には家の顔として応対します。過度な親密さも、必要以上の冷淡さも避け、取次・案内・退出までの順序を乱しません。依頼者不在時は推測で約束を作らず、預かりと確認の手順を踏みます。'
    },
    {
      en: 'Colleagues',
      ja: '他スタッフへの応対',
      text: '同僚や外部スタッフには、敬意と明確さを両立させます。指示系統が交差する場面では、誰の何の指示に従うかを確認し、場の空気で曖昧に進めません。'
    },
    {
      en: 'Boundaries',
      ja: '距離の一線',
      text: '親しくなった相手でも、守秘と職務の線は更新しません。私的な依頼や噂話に巻き込まれそうなときは、丁寧に職務の範囲へ戻す言葉を用意します。'
    }
  ];

  const recordPrinciples = [
    {
      no: 'A',
      title: '事実と確認済み事項',
      text: '観察した事実、相手が明示した希望、実施した対応のみを残します。憶測・評価・感情のメモは記録に混ぜません。'
    },
    {
      no: 'B',
      title: '必要最小の粒度',
      text: '長く詳しいほど良いわけではありません。次の判断と再現に足りる要点だけを、後から読める順で残します。'
    },
    {
      no: 'C',
      title: '共有範囲の限定',
      text: '閲覧してよい相手を決め、それ以外へは出しません。便利さのための転送や私的な端末への複製は、規範に反する行為です。'
    },
    {
      no: 'D',
      title: '更新と破棄の意識',
      text: '古い情報が残ると誤判断の種になります。更新したら旧版の扱いを明確にし、不要になった記録は定められた方法で処分します。'
    }
  ];

  const handoverFlow = [
    {
      en: 'Frame',
      ja: '枠を渡す',
      text: '今日の未完了、進行中、本日中に動く予定を先に示し、受け手が全体像を掴める順に並べます。'
    },
    {
      en: 'Facts',
      ja: '事実を渡す',
      text: '好み・注意点・鍵や機材の所在など、確認済みの事実だけを簡潔に渡します。感想や人物評は添えません。'
    },
    {
      en: 'Open',
      ja: '未決を明示する',
      text: '判断待ち・返信待ち・曖昧なまま残っている点を隠さず示します。未決を「問題なし」に見せないことが誠実さです。'
    },
    {
      en: 'Confirm',
      ja: '受領を確認する',
      text: '口頭でも文書でも、受け手が要点を把握したかを確認して閉じます。渡したつもりで終わらせません。'
    }
  ];

  const faqItems = [
    {
      q: '指示が曖昧なとき、どこまで自分で補ってよいですか？',
      a: '補ってよいのは、安全と品位を守るための当面の備えまでです。方針に関わる決定や、元に戻せない手配は、復唱と確認を挟んでから進めます。急を要する場合でも、着手と同時に確認の連絡を入れ、権限の外で確定させないことが行動規範の基本です。'
    },
    {
      q: '報告・連絡・相談の使い分けで迷ったら何を優先すべきですか？',
      a: 'まず「相手が今知らないと困る事実」があるかを見ます。それがあるなら報告や連絡を優先し、判断の分岐だけが残っているなら相談の形に整えます。どの場合も、感情の整理より先に、状況・影響・期限を短く置くと伝わりやすくなります。'
    },
    {
      q: '引き継ぎでどこまで詳しく書くべきでしょうか？',
      a: '次の担当が迷わず着手できる粒度を上限にします。経緯の物語や私的な印象は削り、未完了・未決・確認済みの希望・注意点に絞ります。詳述より、読み手が同じ地図を持てることが目的です。'
    }
  ];

  const articleLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: '執事行動規範｜全日本執事協会が定める現場の原則',
    description:
      '全日本執事協会が定める執事行動規範。規範を定める理由、指示の受け方と確認、報告連絡相談、時間厳守と事前準備、私物と職務の分離、来客と他スタッフへの応対、記録の残し方、業務終了時の引き継ぎ。',
    author: {
      '@type': 'Organization',
      name: '一般社団法人 全日本執事協会'
    },
    publisher: {
      '@type': 'Organization',
      name: '一般社団法人 全日本執事協会'
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': 'https://japanbutlerassociation.com/standards/conduct'
    }
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'ホーム', item: 'https://japanbutlerassociation.com/' },
      { '@type': 'ListItem', position: 2, name: '基準・指針', item: 'https://japanbutlerassociation.com/standards' },
      { '@type': 'ListItem', position: 3, name: '執事行動規範', item: 'https://japanbutlerassociation.com/standards/conduct' }
    ]
  };

  const faqLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a
      }
    }))
  };

  return (
    <Layout>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLd) }} />

      {/* ヒーロー — 全幅ビルボード */}
      <section className="relative bg-[#1a2942] overflow-hidden min-h-[520px] lg:min-h-[600px] flex items-center">
        <img
          src="/images/generated/jba-std-conduct-01.jpg"
          alt="静かな邸内で姿勢を整え、職務に臨む執事。行動規範の原点を示す一枚"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-[linear-gradient(100deg,rgba(16,20,26,0.93)_0%,rgba(16,20,26,0.74)_50%,rgba(16,20,26,0.38)_100%)]"></div>
        <div className="relative z-10 w-full max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="max-w-[720px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.32em] text-xs sm:text-[13px] text-[#d7b779] mb-4">
              Code of Conduct
            </span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="jba-display font-normal text-white text-[clamp(28px,3.4vw,44px)] leading-[1.4] tracking-[0.04em] mb-6">
              <span className="block">執事行動規範</span>
              <span className="block text-[#d7b779]">現場で立ち返る行動の原則</span>
            </h1>
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95]">
              一般社団法人 全日本執事協会は、1890年（明治23年）の創立以来、日本における執事文化の継承に取り組んできました。代表理事・石井裕一（三代目頭取）のもと、本規範は認定団体である当協会が定める、現場での行動の原則です。指示の受け方、報告連絡相談、時間と準備、公私の分離、来客と他スタッフへの応対、記録と引き継ぎ——日々の判断が揺れたときに戻るための型を示します。
            </p>
          </div>
        </div>
      </section>

      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      <div className="bg-white">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16 lg:py-20 space-y-20 sm:space-y-24">

          {/* S1. 規範を定める理由 — 引用帯＋三本柱（hospitalityの三指標グリッドとは別型） */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              I. Why a Code
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              規範を定める<span className="text-[#a38e65]">理由</span>
            </h2>
            <div className="border-l-2 border-[#a38e65] bg-[#fbfaf7] px-6 sm:px-8 py-6 mb-10">
              <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#1a2942] leading-[1.95]">
                行動規範は、誰かを縛るための文書ではありません。現場の裁量を残したまま、品位と安全と連続性を守るための共通の骨格です。当協会はAssociate・Professional・Masterの三段階と専門資格を通じて技能を育てると同時に、日々の振る舞いが家の信頼に直結するという認識を、本規範に込めています。
              </p>
            </div>
            <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-10">
              <p>
                倫理の考え方や立ち居振る舞いの細部は、それぞれ
                <a href="/standards/ethics" className="text-[#1a2942] underline underline-offset-4 decoration-[#a38e65] mx-1">倫理規定</a>
                および
                <a href="/standards/deportment" className="text-[#1a2942] underline underline-offset-4 decoration-[#a38e65] mx-1">立ち居振る舞いの基準</a>
                と併せて参照してください。基準全体の見取り図は
                <a href="/standards" className="text-[#1a2942] underline underline-offset-4 decoration-[#a38e65] mx-1">基準・指針</a>
                から俯瞰できます。ここでは、現場で手足を動かす直前の「受け方・伝え方・残し方」に焦点を当てます。
              </p>
            </div>
            <div className="grid lg:grid-cols-3 gap-6">
              {reasonPillars.map((p) => (
                <div key={p.en} className="relative bg-white border border-[#e2ddd2] shadow-[0_12px_40px_rgba(26,41,66,0.06)] px-6 py-8 overflow-hidden">
                  <span className="absolute top-0 right-0 font-['Playfair_Display',serif] text-[64px] leading-none text-[#a38e65]/15 pr-3 pt-1 select-none">{p.en.charAt(0)}</span>
                  <span className="block font-['Playfair_Display',serif] italic text-[#8f7a56] text-[12px] tracking-[0.08em] mb-2">{p.en}</span>
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl mb-3">{p.ja}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{p.text}</p>
                </div>
              ))}
            </div>
          </section>

          {/* S2. 指示の受け方 — 縦タイムライン型（横四ステップとは別） */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              II. Receiving Direction
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              指示の受け方と確認の型
            </h2>
            <div className="grid lg:grid-cols-[340px_1fr] gap-10 items-start mb-10">
              <img
                src="/images/generated/jba-std-conduct-02.jpg"
                alt="指示の要点を静かに確認する執事たちのブリーフィング"
                className="w-full h-64 lg:h-full min-h-[280px] object-cover border border-[#a38e65]/30"
              />
              <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95]">
                <p>
                  優れた遂行は、優れた着手の前に、優れた受信から始まります。指示を「聞いた気になる」ことと、実行可能な形に落とすことは別の技能です。当協会が重視するのは、相手の言葉を尊重しつつ、曖昧さをその場で減らし、権限の外へ踏み出さない確認の型です。
                </p>
                <p>
                  急かされる場面ほど、復唱を省略したくなります。しかし省略された確認は、後から倍の説明と謝罪を生むことが多いものです。短くてもよいので、目的・期限・成果の形・例外時の連絡先を揃えてから動き出します。
                </p>
              </div>
            </div>
            <div className="relative border-l border-[#a38e65]/50 ml-3 sm:ml-4 space-y-0">
              {instructionSteps.map((s) => (
                <div key={s.no} className="relative pl-8 sm:pl-10 pb-8 last:pb-0">
                  <span className="absolute left-0 top-1 -translate-x-1/2 w-6 h-6 rounded-full bg-[#1a2942] border-2 border-[#a38e65] flex items-center justify-center">
                    <span className="w-1.5 h-1.5 bg-[#d7b779] rounded-full"></span>
                  </span>
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-2">
                    <span className="font-['Playfair_Display',serif] text-[#d7b779] text-sm tracking-[0.12em]">{s.no}</span>
                    <span className="font-['Playfair_Display',serif] italic text-[#8f7a56] text-sm">{s.en}</span>
                    <h3 className="jba-display font-medium text-[#1a2942] text-lg">{s.ja}</h3>
                  </div>
                  <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.9]">{s.text}</p>
                </div>
              ))}
            </div>
          </section>

          {/* S3. 報告連絡相談 — 横帯見出し＋三枚の縦長カード */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              III. Report · Inform · Consult
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-3">
              報告・連絡・相談の型
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-8 max-w-3xl">
              報連相は形式的な儀式ではありません。関係者が同じ現実を共有し、判断の遅れと重複を防ぐための呼吸です。多すぎる連絡は場を騒がせ、少なすぎる報告は信頼を削ります。量ではなく、時機と粒度の適切さが規範の要点です。
            </p>
            <div className="bg-[#1a2942] px-6 sm:px-8 py-3 mb-0 flex flex-wrap items-center gap-x-6 gap-y-2">
              <span className="font-['Playfair_Display',serif] text-[#d7b779] tracking-[0.18em] text-sm">CADENCE</span>
              <span className="font-['Noto_Serif_JP',serif] text-[#f8f6f0]/85 text-sm tracking-[0.06em]">
                事実を先に、解釈を後に、相談は論点を整えて
              </span>
            </div>
            <div className="grid md:grid-cols-3 gap-px bg-[#a38e65]/35 border border-[#a38e65]/35">
              {houRenSou.map((block) => (
                <div key={block.title} className="bg-[#fbfaf7] p-7 sm:p-8">
                  <h3 className="jba-display font-medium text-[#1a2942] text-xl mb-2">{block.title}</h3>
                  <p className="font-['Playfair_Display',serif] italic text-[#8f7a56] text-sm mb-4">{block.lead}</p>
                  <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#333] leading-[1.95]">{block.body}</p>
                </div>
              ))}
            </div>
            <div className="mt-8">
              <img
                src="/images/generated/jba-std-conduct-03.jpg"
                alt="報告前に事実関係を手元で点検する執事"
                className="w-full h-56 sm:h-64 object-cover border border-[#a38e65]/30"
              />
            </div>
          </section>

          {/* S4. 時間厳守と事前準備 — 三列の細則カード＋下部リード */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              IV. Time & Preparation
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              時間厳守と事前準備
            </h2>
            <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-10">
              <p>
                時間を守ることは、時計の針に従うこと以上の意味を持ちます。相手の予定、場の流れ、後続の手配までを含めた設計として時間を扱う。遅刻の有無だけでなく、「待たせない静けさ」を目指すのが執事の時間感覚です。
              </p>
              <p>
                事前準備は、当日の慌てを消すためのものです。ただし準備の痕跡が目立ちすぎると、かえって場が固くなります。見えないところまで整え、表に出る瞬間だけが自然である——そのバランスが行動規範の求める準備です。
              </p>
            </div>
            <div className="grid lg:grid-cols-3 gap-6">
              {timePrepAxes.map((col) => (
                <div key={col.label} className="border-t-2 border-[#1a2942] bg-white border-x border-b border-x-[#e2ddd2] border-b-[#e2ddd2] p-6 sm:p-7">
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-4">{col.label}</h3>
                  <ul className="space-y-3">
                    {col.lines.map((line) => (
                      <li key={line} className="flex gap-3 font-['Noto_Serif_JP',serif] text-[14px] text-[#333] leading-[1.85]">
                        <span className="text-[#a38e65] shrink-0 mt-0.5">▸</span>
                        <span>{line}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>

          {/* S5. 私物と職務の分離 — 左右対比パネル＋画像帯 */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              V. Personal & Professional
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              私物と職務の分離
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-8 max-w-3xl">
              公私の混線は、大きな事故の前に小さな違和感として現れます。私的な話題が応対に混ざる、個人の都合が動線を歪める、職務で得た情報を雑談の材料にする——いずれも信頼を静かに侵食します。分離とは冷たさではなく、相手の領域を守るための礼儀です。
            </p>
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {separationNotes.map((block) => (
                <div key={block.side} className="bg-[#1a2942] p-7 sm:p-8">
                  <h3 className="jba-display font-medium text-[#d7b779] text-lg mb-5 pb-3 border-b border-[#a38e65]/40">{block.side}</h3>
                  <ul className="space-y-4">
                    {block.items.map((item) => (
                      <li key={item} className="flex gap-3 font-['Noto_Serif_JP',serif] text-[15px] text-[#f8f6f0] leading-[1.85]">
                        <span className="text-[#a38e65] shrink-0">—</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <div className="grid lg:grid-cols-[1fr_1.1fr] gap-8 items-center">
              <img
                src="/images/gen2/std1-conduct-posture-training.jpg"
                alt="職務の基本姿勢を講師と確認する執事研修"
                className="w-full h-52 sm:h-64 object-cover border border-[#a38e65]/30"
              />
              <div className="border border-[#e2ddd2] bg-[#fbfaf7] p-6 sm:p-8">
                <p className="font-['Playfair_Display',serif] italic text-[#8f7a56] text-sm mb-3">Quiet separation</p>
                <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#333] leading-[1.95]">
                  勤務の開始と終了で、意識のスイッチを明確にします。私的な通信は定められた時間と場所に限り、職務空間では家の流儀と依頼者の都合を最優先する。この反復が、長い奉仕関係の清潔さを支えます。
                </p>
              </div>
            </div>
          </section>

          {/* S6. 来客と他スタッフ — 三枚の横長シーンカード */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              VI. Guests & Staff
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              来客と他スタッフへの応対
            </h2>
            <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-10">
              <p>
                行動規範は、依頼者との一対一だけを想定したものではありません。来客、同居するスタッフ、外部の業者や臨時の協力者——複数の立場が交差する場でこそ、応対の一貫性が問われます。誰に対しても丁寧であることと、誰に対しても同じ距離であることは異なります。役割に応じた敬意の形を、過不足なく選ぶことが求められます。
              </p>
            </div>
            <div className="space-y-5 mb-8">
              {relationScenes.map((scene, idx) => (
                <div key={scene.en} className="grid sm:grid-cols-[100px_1fr] gap-4 sm:gap-6 border border-[#e2ddd2] bg-white p-6 sm:p-7 shadow-[0_8px_28px_rgba(26,41,66,0.04)]">
                  <div className="flex sm:flex-col items-baseline sm:items-start gap-2 sm:gap-1">
                    <span className="font-['Playfair_Display',serif] text-[#d7b779] text-2xl">{String(idx + 1).padStart(2, '0')}</span>
                    <span className="font-['Playfair_Display',serif] italic text-[#8f7a56] text-xs tracking-[0.08em]">{scene.en}</span>
                  </div>
                  <div>
                    <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-2">{scene.ja}</h3>
                    <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.9]">{scene.text}</p>
                  </div>
                </div>
              ))}
            </div>
            <img
              src="/images/gen2/std1-conduct-guest-staff-flow.jpg"
              alt="来客とスタッフの動線を静かに整える執事"
              className="w-full h-56 sm:h-72 object-cover border border-[#a38e65]/30"
            />
          </section>

          {/* S7. 記録の残し方＋業務終了時の引き継ぎ — 統合セクション */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              VII. Keeping Records & Handover
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              記録の残し方
            </h2>
            <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-10 items-start mb-10">
              <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95]">
                <p>
                  記録は、記憶の補助であり、交代を支える装置です。しかし書きすぎは守秘を危うくし、書かなすぎは再現性を損ないます。行動規範における記録とは、奉仕の精度を保つために必要な最小限の事実を、正しい相手だけが読める形で残す行為です。
                </p>
                <p>
                  媒体が紙であれ電子であれ、原則は変わりません。見出しを立て、時系列を乱さず、誰の発言かと何を実施したかを混同しない。美しさより先に、誤解の余地を減らすことを優先します。
                </p>
              </div>
              <img
                src="/images/gen2/std1-conduct-record-handover.jpg"
                alt="記録と引き継ぎを統括する上級執事"
                className="w-full h-56 lg:h-[280px] object-cover border border-[#a38e65]/30"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-5 mb-12">
              {recordPrinciples.map((r) => (
                <div key={r.no} className="flex gap-4 border border-[#e2ddd2] bg-[#fbfaf7] p-6">
                  <span className="font-['Playfair_Display',serif] text-[#a38e65] text-2xl shrink-0 w-8">{r.no}</span>
                  <div>
                    <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-2">{r.title}</h3>
                    <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{r.text}</p>
                  </div>
                </div>
              ))}
            </div>
            <h3 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              業務終了時の引き継ぎ
            </h3>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-8 max-w-3xl">
              一日の終わり、休暇前、担当交代——いずれの区切りでも、引き継ぎは「自分の仕事が終わった」合図ではなく、「次の奉仕が滞りなく始まる」ための渡し方です。口頭の温度に頼らず、受け手が同じ地図を持てる形で閉じることが、行動規範の仕上げにあたります。
            </p>
            <ol className="grid sm:grid-cols-2 border-t-2 border-[#1a2942] mb-10">
              {handoverFlow.map((h, i) => (
                <li
                  key={h.en}
                  className={`border-b border-[#e2ddd2] px-5 py-6 ${
                    i % 2 === 0 ? 'sm:border-r' : ''
                  }`}
                >
                  <span className="block font-['Playfair_Display',serif] text-[#a38e65] text-sm tracking-[0.12em] mb-2">{h.en}</span>
                  <span className="block jba-display font-medium text-[#1a2942] text-lg mb-3">{h.ja}</span>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{h.text}</p>
                </li>
              ))}
            </ol>
            <div className="bg-[#fbfaf7] border border-[#e2ddd2] p-7 sm:p-8">
              <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#333] leading-[1.95]">
                引き継ぎの席で評価や不満を混ぜると、次の担当の目が曇ります。事実と未決と確認済みの希望だけを渡す。それが、個人の感情を超えた職能としての区切り方です。Master Butlerが示す統括の視点も、こうした現場の受け渡しが滞りなく回る設計に表れます。資格の段階にかかわらず、渡し切る力はすべての執事に求められます。
              </p>
            </div>
          </section>

          {/* S8. FAQ */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              IX. Questions
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              よくある問い
            </h2>
            <div className="border-t-2 border-[#1a2942]">
              {faqItems.map((item) => (
                <div key={item.q} className="border-b border-[#e2ddd2] py-6">
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-3 flex gap-3">
                    <span className="text-[#a38e65] font-['Playfair_Display',serif] shrink-0">Q</span>
                    <span>{item.q}</span>
                  </h3>
                  <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#333] leading-[1.95] pl-8">
                    {item.a}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* S9. 関連導線 */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              X. Related Paths
            </span>
            <h3 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              関連する基準と学びへ
            </h3>
            <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 lg:p-12">
              <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#e7e2d8] leading-[1.95] max-w-3xl mb-8">
                行動規範は、倫理の考え方や立ち居振る舞いの基準、そして資格制度における段階的な学びと合わせて初めて現場で生きます。全日本執事協会は、1890年（明治23年）から続く系譜のうえで、いまの暮らしにふさわしい奉仕の骨格を示し続けます。判断に迷ったときは、個別の技法より先に、受け方・伝え方・残し方の原則へ戻ってください。
              </p>
              <div className="flex flex-col sm:flex-row flex-wrap gap-4 sm:gap-6">
                <a href="/standards/ethics" className={BTN08_ON_NAVY}>
                  倫理規定を読む
                </a>
                <a href="/standards/deportment" className={BTN08_ON_NAVY}>
                  立ち居振る舞いの基準
                </a>
                <a href="/qualifications" className={BTN08_ON_NAVY}>
                  資格制度を見る
                </a>
              </div>
              <div className="mt-6">
                <a href="/standards" className={BTN08_ON_LIGHT}>
                  基準・指針の一覧
                </a>
              </div>
            </div>
          </section>

        </div>
      </div>
    </Layout>
  );
}
